import { useEffect, useId, useRef, useState } from 'react';
import { Mail, RefreshCw, Send, UserCog, UserMinus, X } from 'lucide-react';
import { formatarData, PerfilPadrao, type ConvitePendenteDto, type MembroDoQuadroDto, type MembrosDoQuadroDto } from '@nexital/shared';
import { useDependencias, useEnvio } from '../../application/dependencias';
import { Avatar } from '../design-system/Avatar';
import { useConfirmar, usePedirMotivo } from '../design-system/Confirmacao';
import './configuracoes.css';

const PAPEIS: { perfil: PerfilPadrao; rotulo: string }[] = [
  { perfil: PerfilPadrao.Administrador, rotulo: 'Administrador' },
  { perfil: PerfilPadrao.Gestor, rotulo: 'Gestor' },
  { perfil: PerfilPadrao.Colaborador, rotulo: 'Colaborador' },
  { perfil: PerfilPadrao.Leitor, rotulo: 'Leitor' },
];
const COM_GESTOR = new Set([PerfilPadrao.Colaborador, PerfilPadrao.Leitor]);

const SEM_REENVIO: Record<ConvitePendenteDto['situacao'], string> = {
  vencido: 'O prazo acabou: envie um convite novo.',
  'aguardando-aprovacao': 'Ainda não foi enviado: aguarda a aprovação do gestor.',
  enviado: 'Já foi reenviado.',
};

const SITUACOES: Record<ConvitePendenteDto['situacao'], { rotulo: string; pilula: string }> = {
  'aguardando-aprovacao': { rotulo: 'Aguardando aprovação do gestor', pilula: 'pilula--aguardando' },
  enviado: { rotulo: 'Enviado', pilula: 'pilula--informacao' },
  vencido: { rotulo: 'Vencido', pilula: 'pilula--urgencia' },
};

type Acoes = { gravar: (acao: () => Promise<unknown>, mensagem: string) => Promise<void>; dados: MembrosDoQuadroDto };

/** Papel novo e, para colaborador e leitor, o gestor direto (vazio: mantém o atual ou fica com quem mudou). */
function EditorPapel({ m, dados, gravar, fechar }: Acoes & { m: MembroDoQuadroDto; fechar: () => void }) {
  const { espacos } = useDependencias();
  const confirmar = useConfirmar();
  const [perfil, definirPerfil] = useState(m.perfilPadrao ?? PerfilPadrao.Colaborador);
  const [gestor, definirGestor] = useState('');
  const rotulo = PAPEIS.find((p) => p.perfil === perfil)!.rotulo;
  const salvar = async () => {
    if (!(await confirmar({ titulo: `Mudar o papel de ${m.nome}?`, mensagem: `${m.nome} passa a ser ${rotulo} neste quadro e recebe um aviso no sino.`, rotulo: 'Mudar papel', perigo: false }))) return;
    await gravar(() => espacos.membros.mudarPapel(m.uuid, { perfil, gestorUuid: COM_GESTOR.has(perfil) && gestor ? gestor : undefined }), `${m.nome} agora é ${rotulo}.`);
    fechar();
  };
  return (
    <div className="membros__editor">
      <label className="campo">
        <span>Papel</span>
        <select value={perfil} onChange={(e) => definirPerfil(Number(e.target.value) as PerfilPadrao)}>
          {PAPEIS.map((p) => (
            <option key={p.perfil} value={p.perfil}>
              {p.rotulo}
            </option>
          ))}
        </select>
      </label>
      {COM_GESTOR.has(perfil) && dados.gestores.length > 0 && (
        <label className="campo">
          <span>Gestor direto</span>
          <select value={gestor} onChange={(e) => definirGestor(e.target.value)}>
            <option value="">{COM_GESTOR.has(m.perfilPadrao ?? PerfilPadrao.Administrador) ? 'Manter o atual' : 'Eu mesmo (administrador)'}</option>
            {dados.gestores
              .filter((g) => g.uuid !== m.uuid)
              .map((g) => (
                <option key={g.uuid} value={g.uuid}>
                  {g.nome}
                </option>
              ))}
          </select>
        </label>
      )}
      <div className="config__grupo-acoes">
        <button type="button" className="botao botao--secundario" onClick={fechar}>
          Cancelar
        </button>
        <button type="button" className="botao botao--primario" disabled={perfil === m.perfilPadrao} onClick={() => void salvar()}>
          Salvar
        </button>
      </div>
    </div>
  );
}

/** Trocar o gestor direto, mudar o papel e remover, conforme o que quem vê pode fazer. */
function AcoesMembro({ m, dados, gravar, editar }: Acoes & { m: MembroDoQuadroDto; editar: (() => void) | null }) {
  const { espacos } = useDependencias();
  const pedirMotivo = usePedirMotivo();
  const outrosGestores = dados.gestores.filter((g) => g.uuid !== m.gestor?.uuid && g.uuid !== m.uuid);
  const remover = async () => {
    const motivo = await pedirMotivo({
      titulo: `Remover ${m.nome} do quadro?`,
      mensagem: 'A pessoa perde o acesso a este quadro na hora. Quem estava ligado a ela passa para você.',
      rotulo: 'Remover',
    });
    if (motivo) await gravar(() => espacos.membros.remover(m.uuid, motivo), `${m.nome} foi removido do quadro.`);
  };
  const trocarGestor = (gestorUuid: string) => {
    const nome = dados.gestores.find((g) => g.uuid === gestorUuid)?.nome;
    void gravar(() => espacos.membros.trocarGestor(m.uuid, gestorUuid), `${nome} agora é o gestor direto de ${m.nome}.`);
  };
  return (
    <div className="config__acoes-linha">
      {m.podeTrocarGestor && outrosGestores.length > 0 && (
        <select className="membros__trocar" aria-label={`Trocar o gestor direto de ${m.nome}`} value="" onChange={(e) => trocarGestor(e.target.value)}>
          <option value="" disabled>
            Trocar gestor…
          </option>
          {outrosGestores.map((g) => (
            <option key={g.uuid} value={g.uuid}>
              {g.nome}
            </option>
          ))}
        </select>
      )}
      {dados.podeMudarPapel && !m.voce && editar && (
        <button type="button" className="botao-icone" aria-label={`Mudar o papel de ${m.nome}`} title="Mudar papel" onClick={editar}>
          <UserCog size={16} />
        </button>
      )}
      {dados.podeRemover && !m.voce && (
        <button type="button" className="botao-icone" aria-label={`Remover ${m.nome} do quadro`} title="Remover do quadro" onClick={() => void remover()}>
          <UserMinus size={16} />
        </button>
      )}
    </div>
  );
}

function Membro({ m, dados, gravar }: Acoes & { m: MembroDoQuadroDto }) {
  const [editando, definirEditando] = useState(false);
  return (
    <li className="config__usuario membros__linha">
      <Avatar nome={m.nome} chave={m.uuid} tamanho={36} url={m.avatarUrl} />
      <div className="config__usuario-dados">
        <strong>
          {m.nome}
          {m.voce && ' (você)'}
        </strong>
        <span>{m.email}</span>
        <span className="config__usuario-quadros">
          <span className="pilula pilula--informacao">{m.perfil}</span>
          {m.gestor && <small>Gestor direto: {m.gestor.nome}</small>}
        </span>
        {editando && <EditorPapel m={m} dados={dados} gravar={gravar} fechar={() => definirEditando(false)} />}
      </div>
      <AcoesMembro m={m} dados={dados} gravar={gravar} editar={editando ? null : () => definirEditando(true)} />
    </li>
  );
}

function Convite({ c, dados, gravar }: Acoes & { c: ConvitePendenteDto }) {
  const { espacos } = useDependencias();
  const pedirMotivo = usePedirMotivo();
  const situacao = SITUACOES[c.situacao];
  const cancelar = async () => {
    const motivo = await pedirMotivo({
      titulo: `Cancelar o convite de ${c.email}?`,
      mensagem: c.situacao === 'aguardando-aprovacao' ? 'O convite ainda não tinha sido enviado; nada chega ao convidado.' : 'O link deixa de funcionar e o convidado recebe um e-mail dizendo que o convite foi revogado.',
      rotulo: 'Cancelar convite',
    });
    if (motivo) await gravar(() => espacos.membros.cancelarConvite(c.uuid, motivo), `Convite de ${c.email} cancelado.`);
  };
  return (
    <li className="config__usuario membros__linha">
      <span className="config__cartao-icone">
        <Mail size={18} />
      </span>
      <div className="config__usuario-dados">
        <strong>{c.email}</strong>
        <span className="config__usuario-quadros">
          <span className="pilula pilula--informacao">{c.perfil}</span>
          <span className={`pilula ${situacao.pilula}`}>{situacao.rotulo}</span>
        </span>
        <small>
          Convidado por {c.convidadoPor} em {formatarData(c.criadoEm)} · {c.situacao === 'vencido' ? 'venceu' : 'vale até'} {formatarData(c.expiraEm)}
          {c.gestor && ` · gestor direto: ${c.gestor}`} · reenvios: {c.reenvios} de {dados.limiteReenvios}
        </small>
      </div>
      <div className="config__acoes-linha">
        {c.podeConvidarDeNovo ? (
          <button type="button" className="botao botao--secundario" onClick={() => void gravar(() => espacos.membros.convidarDeNovo(c.uuid), `Convite novo enviado para ${c.email}.`)}>
            <Send size={14} aria-hidden /> Enviar convite novo
          </button>
        ) : (
          <button
            type="button"
            className="botao botao--secundario"
            disabled={!c.podeReenviar}
            title={c.podeReenviar ? 'Reenvia o e-mail; o prazo continua o mesmo' : SEM_REENVIO[c.situacao]}
            onClick={() => void gravar(() => espacos.membros.reenviarConvite(c.uuid), `Convite reenviado para ${c.email}.`)}
          >
            <RefreshCw size={14} aria-hidden /> Reenviar
          </button>
        )}
        {c.podeCancelar && (
          <button type="button" className="botao-icone" aria-label={`Cancelar o convite de ${c.email}`} title="Cancelar convite" onClick={() => void cancelar()}>
            <X size={16} />
          </button>
        )}
      </div>
    </li>
  );
}

/** Título com o X de fechar no canto superior direito. */
function Cabecalho({ id, quadro, aoFechar }: { id: string; quadro?: string; aoFechar: () => void }) {
  return (
    <header className="membros__cabecalho">
      <h2 id={id} className="dialogo__titulo">
        Membros do quadro{quadro && ` ${quadro}`}
      </h2>
      <button type="button" className="botao-icone" aria-label="Fechar" title="Fechar" onClick={aoFechar}>
        <X size={18} />
      </button>
    </header>
  );
}

/** Carrega a lista e, a cada ação, avisa o resultado e recarrega. */
function useMembros() {
  const { espacos } = useDependencias();
  const avisar = useEnvio((s) => s.avisar);
  const [dados, definir] = useState<MembrosDoQuadroDto | null>(null);
  const [erro, definirErro] = useState<string>();
  useEffect(() => {
    espacos.membros.listar().then(definir, (e: Error) => definirErro(e.message));
  }, [espacos]);
  const gravar = async (acao: () => Promise<unknown>, mensagem: string) => {
    try {
      await acao();
      avisar(mensagem);
      definir(await espacos.membros.listar());
    } catch (e) {
      avisar((e as Error).message);
    }
  };
  return { dados, erro, gravar };
}

/**
 * Membros do quadro (RQ-ACE-018): quem está no quadro, o gestor direto (troca o administrador ou o gestor atual),
 * o papel e a remoção (só o administrador) e os convites pendentes (reenviar uma vez no prazo, convite novo quando
 * vence, cancelar com aviso ao convidado).
 */
export function MembrosDoQuadro({ aoFechar }: { aoFechar: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const { dados, erro, gravar } = useMembros();
  useEffect(() => {
    const dialogo = ref.current;
    if (dialogo && !dialogo.open) dialogo.showModal();
    return () => dialogo?.close();
  }, []);

  return (
    <dialog
      ref={ref}
      className="dialogo membros"
      aria-labelledby={`${id}-titulo`}
      onCancel={(evento) => {
        evento.preventDefault();
        aoFechar();
      }}
    >
      <Cabecalho id={`${id}-titulo`} quadro={dados?.quadro} aoFechar={aoFechar} />
      {erro && (
        <p className="cartao-acesso__erro" role="alert">
          {erro}
        </p>
      )}
      {!dados && !erro && <p>Carregando…</p>}
      {dados && (
        <>
          <h3 className="membros__secao">Pessoas ({dados.membros.length})</h3>
          <ul className="config__usuarios">
            {dados.membros.map((m) => (
              <Membro key={m.uuid} m={m} dados={dados} gravar={gravar} />
            ))}
          </ul>
          {dados.convites.length > 0 && (
            <>
              <h3 className="membros__secao">Convites pendentes ({dados.convites.length})</h3>
              <ul className="config__usuarios">
                {dados.convites.map((c) => (
                  <Convite key={c.uuid} c={c} dados={dados} gravar={gravar} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
      <footer className="dialogo__acoes">
        <button type="button" className="botao botao--primario" onClick={aoFechar}>
          Fechar
        </button>
      </footer>
    </dialog>
  );
}
